import type {
  PosFiscalDocument,
  PosFiscalDocumentSummary,
  PosInvoiceCustomer,
  PosReceivableCustomerMissing
} from '@/types/PosTransaction.interface';

/** Anything that carries POS transaction `metadata` (loosely typed JSON). */
interface WithMetadata {
  metadata?: Record<string, unknown> | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const asTextOrNumber = (value: unknown): string | number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return asText(value);
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/** `metadata.fiscalDocument`, or undefined when absent or malformed. */
export const getFiscalDocument = (
  transaction: WithMetadata
): PosFiscalDocument | undefined => {
  const raw = transaction.metadata?.fiscalDocument;
  if (!isRecord(raw)) return undefined;
  return {
    invoiceID: asText(raw.invoiceID),
    documentType: asTextOrNumber(raw.documentType),
    uuid: asText(raw.uuid),
    instanceIdentifier: asText(raw.instanceIdentifier),
    alias: asText(raw.alias),
    receiptNo: asTextOrNumber(raw.receiptNo),
    zNo: asTextOrNumber(raw.zNo)
  };
};

/** `metadata.invoiceCustomer` (the buyer), or undefined when it names nobody. */
export const getInvoiceCustomer = (
  transaction: WithMetadata
): PosInvoiceCustomer | undefined => {
  const raw = transaction.metadata?.invoiceCustomer;
  if (!isRecord(raw)) return undefined;
  const name = asText(raw.name);
  const taxId = asText(raw.taxId);
  if (!name && !taxId) return undefined;
  return { profileId: asNumber(raw.profileId), taxId, name };
};

/**
 * `metadata.receivableCustomerMissing`: an Açık Hesap fiş was approved without
 * a customer. Present (even without an amount) means it needs attention.
 */
export const getReceivableCustomerMissing = (
  transaction: WithMetadata
): PosReceivableCustomerMissing | undefined => {
  const raw = transaction.metadata?.receivableCustomerMissing;
  if (!isRecord(raw)) return undefined;
  return { amount: asNumber(raw.amount) };
};

/**
 * What to show as "the document the device printed": the e-Belge number when
 * the device returned one, otherwise `Fiş No` / `Z No`. Undefined when the
 * device reported neither.
 */
export const summarizeFiscalDocument = (
  document: PosFiscalDocument | undefined
): PosFiscalDocumentSummary | undefined => {
  if (!document) return undefined;
  if (document.invoiceID) {
    return { kind: 'eBelge', number: document.invoiceID };
  }
  const receiptNo = asText(document.receiptNo);
  const zNo = asText(document.zNo);
  if (!receiptNo && !zNo) return undefined;
  return { kind: 'receipt', receiptNo, zNo };
};
